import React, { useMemo, useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { EditableTitleComponent } from "../../ui/EditableTitleComponent";
import FavoriteButton from "../../ui/FavoriteButton";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Lock, LockOpen, Loader2 } from "lucide-react";
import { Badge } from "../../ui/badge";
import { formatVersionForDisplay } from "@/lib/utils/versionUtils";
import { Version } from "@/lib/stores/storeTypes";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { fetchStatuses, StatusWorkflow } from "@/lib/stores/statusStore";
import DisplayLanguageSelector from "./DisplayLanguageSelector";
type WizardMode = "create" | "edit" | "view";

interface BlueprintInfo {
  id: string;
  name: string;
  version?: 
    | { version: number; changes?: Record<string, unknown> }
    | { major: number; minor: number; patch: number };
}

interface TagOption {
  id: string;
  label: string;
}

interface UnifiedWizardHeaderProps {
  // Common props
  title: string;
  mode: WizardMode;
  itemId?: string;
  itemType: "blueprint" | "library" | "dialogs"| "document"| "variable";
  

  // Action handlers
  onTitleChange?: (newTitle: string) => void;
  onCancel: () => void;
  onRevert?: () => void; // Optional: separate handler for reverting changes (keeps tab open)
  onSubmit?: (...args: any[]) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  
  // State props
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  submitText?: string;
  hasUnsavedChanges?: boolean; // Whether to show Save/Cancel in edit mode
  
  // Metadata props
  tags?: string[]; // Can be either tag IDs or tag names
  tagOptions?: TagOption[]; // For resolving tag IDs to labels (used in blueprints)
  identifiers?: string[];
  version?: number | Version | { version: number; changes?: Record<string, unknown> } | { major: number; minor: number; patch: number };
  blueprint?: BlueprintInfo | null;
  isGlossaryItem?: boolean;
  
  // Owner and permissions
  owner?: string | null;
  editPermissions?: 'Open' | 'Locked';
  canEdit?: boolean; // Whether the current user can edit this item
  
  // Status
  status?: string;
  onStatusChange?: (status: string) => void;
  
  // Optional elements
  additionalBadges?: React.ReactNode;
}

export const UnifiedWizardHeader: React.FC<UnifiedWizardHeaderProps> = ({
  title,
  mode,
  itemId,
  itemType,
  onTitleChange,
  onCancel,
  onRevert,
  onSubmit,
  onEdit,
  onDelete,
  isSubmitting = false,
  submitDisabled = false,
  submitText = itemType === "blueprint" ? "Create Template" : "Create Library Object",
  hasUnsavedChanges = false,
  tags,
  tagOptions,
  identifiers,
  version,
  blueprint,
  isGlossaryItem,
  additionalBadges,
  owner,
  editPermissions,
  canEdit = true,
  status,
  onStatusChange,
}) => {
  const navigate = useNavigate();
  const { t } = usePlatformTranslation();
  const { selectedGame, selectedCompany, getGameMembers, currentUser } = useSimplifiedUserData();
  
  // State for available statuses
  const [availableStatuses, setAvailableStatuses] = useState<StatusWorkflow>({});
  
  // Fetch available statuses for all item types
  useEffect(() => {
    const loadStatuses = async () => {
      if (!selectedCompany) return;
      
      try {
        const statuses = await fetchStatuses(
          selectedCompany.id,
          selectedGame?.id
        );

        // Find game-specific or company default
        let config = null;
        
        if (selectedGame) {
          // Look for game-specific config
          config = statuses.find(s => s.game_id === selectedGame.id) || null;
          
          // If no game-specific, use company default
          if (!config) {
            config = statuses.find(s => s.game_id === null) || null;
          }
        } else {
          // No game selected, use company default
          config = statuses.find(s => s.game_id === null) || null;
        }

        if (config) {
          // Determine which workflow to use based on item type
          let workflow: StatusWorkflow = {};
          
          if (itemType === 'blueprint') {
            // Blueprints use the templates workflow
            workflow = config.templates || {};
          } else if (itemType === 'document') {
            // Documents use the documents workflow
            workflow = config.documents || {};
          } else if (itemType === 'library') {
            // Library items: check if it's a document or glossary item
            if (isGlossaryItem === false) {
              // Documents use the documents workflow
              workflow = config.documents || {};
            } else {
              // Glossary items use the objects workflow
              workflow = config.objects || {};
            }
          } else if (itemType === 'dialogs') {
            // Dialogs use the dialogs workflow (or objects as fallback)
            workflow = config.dialogs || config.objects || {};
          }
          
          setAvailableStatuses(workflow);
        } else {
          // Fallback to draft/published if no config
          setAvailableStatuses({
            draft: { color: "#d3d933" },
            published: { color: "#6e35ff" }
          });
        }
      } catch (error) {
        console.error('Error loading statuses:', error);
        // Fallback to draft/published on error
        setAvailableStatuses({
          draft: { color: "#d3d933" },
          published: { color: "#6e35ff" }
        });
      }
    };

    loadStatuses();
  }, [selectedCompany, selectedGame, itemType, isGlossaryItem]);

  const handleBlueprintClick = () => {
    if (blueprint) {
      navigate(`/templates/${blueprint.id}`);
    }
  };

  // Get owner information for tooltip
  const ownerInfo = useMemo(() => {
    if (!owner || !selectedGame?.id) return null;
    const members = getGameMembers(selectedGame.id);
    const ownerMember = members.find(m => m.id === owner);
    return ownerMember ? (ownerMember.username || ownerMember.email) : null;
  }, [owner, selectedGame?.id, getGameMembers]);

  // Check if user can edit status based on ownership and permissions
  const canEditStatus = useMemo(() => {
    if (!onStatusChange) return false;
    if (mode === 'view') return false;
    
    // If permissions are Open, anyone can edit
    if (editPermissions === 'Open') return true;
    
    // If permissions are Locked, only the owner can edit
    if (editPermissions === 'Locked') {
      return owner === currentUser?.id;
    }
    
    // Default to allowing edit in create mode
    return mode === 'create' || mode === 'edit';
  }, [editPermissions, owner, currentUser?.id, onStatusChange, mode]);

  // Get current status color
  const currentStatusColor = useMemo(() => {
    if (!status || !availableStatuses[status]) {
      return Object.values(availableStatuses)[0]?.color || "#d3d933";
    }
    return availableStatuses[status].color;
  }, [status, availableStatuses]);

  // Render tags - if tagOptions provided, resolve IDs to labels
  const renderTags = () => {
    if (!tags || tags.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag, index) => {
          // If tagOptions provided, try to find the label
          const tagLabel = tagOptions 
            ? tagOptions.find(t => t.id === tag)?.label || tag
            : tag;
          
          return (
            <Badge 
              key={index}
              variant="secondary" 
              className="bg-muted text-muted-foreground text-xs px-2 py-0.5"
            >
              {tagLabel}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3 mt-4 mb-4">
      {/* View Mode */}
      {mode === "view" ? (
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 flex items-center gap-3 flex-wrap">
            {/* Favorite button first */}
            {itemId && (
              <FavoriteButton
                itemId={itemId}
                itemType={itemType}
                size="sm"
                variant="ghost"
              />
            )}
            
            {/* Title */}
            <span className="text-foreground font-medium text-lg">
              {title || <span className="text-muted-foreground italic">{t('platform.cards.widgets.not_set')}</span>}
            </span>

            {/* Version (for blueprints) */}
            {version !== undefined && (
              <span className="text-muted-foreground text-sm font-mono">
                v{
                  // Normalize version to a compatible type for formatVersionForDisplay
                  typeof version === "number"
                    ? formatVersionForDisplay(version)
                    : "major" in version && "minor" in version && "patch" in version
                    ? formatVersionForDisplay({
                        major: version.major,
                        minor: version.minor,
                        patch: version.patch,
                      })
                    : "version" in version && typeof version.version === "number"
                    ? formatVersionForDisplay(version.version)
                    : ""
                }
              </span>
            )}

            {/* Blueprint link (for glossary items) */}
            {blueprint && (
              <div 
                onClick={handleBlueprintClick}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-1/10 border border-purple-1/30 cursor-pointer hover:bg-purple-1/20 transition-colors group"
                title={`Template: ${blueprint.name} v${
                  blueprint.version
                    ? typeof blueprint.version === "object" && "version" in blueprint.version && typeof blueprint.version.version === "number"
                      ? formatVersionForDisplay(blueprint.version.version)
                      : "major" in blueprint.version && "minor" in blueprint.version && "patch" in blueprint.version
                      ? formatVersionForDisplay({
                          major: blueprint.version.major,
                          minor: blueprint.version.minor,
                          patch: blueprint.version.patch,
                        })
                      : ""
                    : ""
                }`}
              >
                <span className="text-sm font-medium text-purple-1 group-hover:text-purple-800 dark:group-hover:text-purple-300">
                  Template
                </span>
                <ExternalLink className="w-3.5 h-3.5 text-purple-1 group-hover:text-purple-700 dark:group-hover:text-purple-300" />
              </div>
            )}

            {/* Type badge (for blueprints) */}
            {isGlossaryItem !== undefined && (
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs px-2 py-0.5">
                {isGlossaryItem ? "Glossary" : "Document"}
              </Badge>
            )}

         

            {/* Identifiers (for glossary items) */}
            {identifiers && identifiers.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {identifiers.map((identifier, index) => (
                  <span 
                    key={index}
                    className="bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs px-2 py-0.5 rounded font-mono"
                  >
                    {identifier}
                  </span>
                ))}
              </div>
            )}

            {/* Additional custom badges */}
            {additionalBadges}
          </div>

          {/* View mode buttons */}
          <div className="flex gap-2 flex-shrink-0 items-center">
            <DisplayLanguageSelector itemType={itemType} />
            {onEdit && canEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit}>
                {t('platform.common.edit')}
              </Button>
            )}
            {onEdit && !canEdit && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center px-3 py-2">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <div className="font-medium">Locked</div>
                      {ownerInfo && <div className="text-muted-foreground">Owner: {ownerInfo}</div>}
                      {!ownerInfo && owner && <div className="text-muted-foreground">Owner: {owner}</div>}
                      {!owner && <div className="text-muted-foreground">No owner set</div>}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {onDelete && canEdit && (
              <Button variant="default" size="sm" onClick={onDelete}>
                {t('platform.common.delete')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        /* Create/Edit Mode */
        <div className="flex justify-between items-start gap-6">
          <div className="flex items-center gap-3">
            {onTitleChange ? (
              <EditableTitleComponent
                title={title}
                onTitleChange={onTitleChange}
                headerStyle="heading-3"
                disabled={false}
              />
            ) : (
              <h3 className="text-2xl font-semibold">{title}</h3>
            )}
          </div>
          
          <div className="flex gap-3 items-center">
            <DisplayLanguageSelector itemType={itemType} />
            {mode === "create" && onSubmit && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={onSubmit}
                  disabled={isSubmitting || submitDisabled}
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {isSubmitting ? "Creating..." : submitText}
                </Button>
                <Button variant="outline" size="sm" onClick={onCancel}>
                  {t('platform.common.cancel')}
                </Button>
              </>
            )}
            {mode === "edit" && onSubmit && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          variant="default" 
                          size="sm" 
                          onClick={onSubmit} 
                          disabled={isSubmitting || !hasUnsavedChanges}
                        >
                          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          {isSubmitting ? t('platform.common.saving', 'Saving...') : t('platform.common.SaveChanges')}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!hasUnsavedChanges && !isSubmitting && (
                      <TooltipContent>
                        <span>{t('platform.common.noChangesDetected')}</span>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {hasUnsavedChanges && !isSubmitting && (
                  <Button variant="outline" size="sm" onClick={onRevert || onCancel}>
                    {t('platform.common.revert')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

